/** Presentation outcome, separate from the fact that an AG-UI call finished. */
export type ToolOutcome = 'running' | 'succeeded' | 'failed' | 'rejected' | 'unknown';

export interface ToolResultView {
  readonly status: ToolOutcome;
  readonly failureReason?: string;
}

/** Decode the sidecar's escaped data envelope without interpreting any HTML. */
export function toolResultView(content: string | undefined, source: string | undefined): ToolResultView {
  if (!content || content.length > 300_000) {
    return { status: 'unknown' };
  }
  let raw = content;
  if (raw.startsWith('<untrusted-tool-output ')) {
    const start = raw.indexOf('>\n');
    const end = '\n</untrusted-tool-output>';
    if (start < 0 || !raw.endsWith(end)) {
      return { status: 'unknown' };
    }
    raw = raw.slice(start + 2, -end.length).replace(
      /&(?:amp|lt|gt|quot|#x27);/g,
      (entity) => {
        switch (entity) {
          case '&amp;': return '&';
          case '&lt;': return '<';
          case '&gt;': return '>';
          case '&quot;': return '"';
          case '&#x27;': return "'";
          default: return entity;
        }
      },
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { status: 'unknown' };
  }
  if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    let value = parsed as Record<string, unknown>;
    // The registry retains a legacy wrapper for string-valued human decisions.
    if (source === 'client' && value['ok'] === true && typeof value['result'] === 'string') {
      try {
        const decision: unknown = JSON.parse(value['result']);
        if (decision !== null && typeof decision === 'object' && !Array.isArray(decision) &&
            'decision' in decision && (decision.decision === 'approved' || decision.decision === 'rejected')) {
          value = decision as Record<string, unknown>;
        }
      } catch {
        // Ordinary successful text result, not a structured human decision.
      }
    }
    const rejected = value['rejected'] === true || value['decision'] === 'rejected';
    const failed = value['ok'] === false || value['success'] === false ||
      (typeof value['status'] === 'number' && value['status'] >= 400) ||
      value['status'] === 'timeout' || value['status'] === 'error' ||
      (value['error'] !== undefined && value['error'] !== null && value['error'] !== false);
    if (rejected || failed) {
      const reason = [value['reason'], value['error'], value['message']].find(
        (candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0,
      );
      return {
        status: rejected ? 'rejected' : 'failed',
        failureReason: (reason ?? (rejected ? 'Action rejected' : 'Read failed')).slice(0, 512),
      };
    }
    if (source === 'client') {
      return { status: value['ok'] === true || value['decision'] === 'approved' ? 'succeeded' : 'unknown' };
    }
  }
  // Legacy results without relay provenance never become claimed read evidence.
  return { status: source === 'gateway' && parsed !== null && typeof parsed === 'object' ? 'succeeded' : 'unknown' };
}
